import { ErrorMessageBag } from "./ErrorMessageBag.ts";
import { AxiosResponse } from "axios";
import { FieldValidationError } from "./FieldValidationError";
import { GenericApiError } from "./GenericApiError";
import { VueI18n } from "vue-i18n";
import { i18n } from "@/i18n";

/**
 * Represents the structure of an error response from the API.
 * This type is used to parse and handle errors returned by backend services.
 *
 * @property key - A unique identifier for the error type (e.g., 'VALIDATION_ERROR', 'NETWORK_ERROR').
 *                 Used for i18n lookup and programmatic error handling.
 * @property message - A human-readable error message, typically in English.
 *                     Serves as a fallback when i18n translation is not available.
 * @property parameters - A key-value map of dynamic parameters to be interpolated into the error message.
 *                        For example: { "field": "email", "maxLength": "255" }
 */
export type ApiErrorResponse = {
	key: string,
	message: string,
	parameters: { [key: string]: string },
};

/**
 * A specialized error message bag for handling API errors from Axios responses.
 *
 * This class extends `ErrorMessageBag` and provides structured error handling for:
 * - Network errors (connection failures, timeouts)
 * - HTTP 400 Bad Request (validation errors)
 * - HTTP 403 Forbidden (authorization errors)
 * - HTTP 409 Conflict (resource conflicts)
 * - HTTP 500 Internal Server Error
 *
 * @example
 * ```typescript
 * try {
 *   await axios.post('/api/users', userData);
 * } catch (exception) {
 *   const errors = ApiErrorMessageBag.fromAxiosException(exception);
 *
 *   // Check for field-specific errors
 *   const emailError = errors.getForField('email');
 *   if (emailError) {
 *     console.log('Email error:', emailError.toLocaleString(i18n));
 *   }
 *
 *   // Check for generic errors
 *   if (errors.generic) {
 *     console.log('General error:', errors.generic.toLocaleString(i18n));
 *   }
 * }
 * ```
 *
 * @extends ErrorMessageBag
 */
export class ApiErrorMessageBag extends ErrorMessageBag {

	/**
	 * The 'generic' error object is used for errors that are not specific to a single field, but rather apply to
	 * the API request as a whole. This can include things like network errors, authentication errors, or any other
	 * error that doesn't fit into the field-specific validation errors.
	 *
	 * Use this object in case you are also displaying the errors of all fields.
	 * If not, rather use the APiErrorMessageBag directly to show all errors, including the generic ones,
	 * but also including the field-specific ones.
	 */
	public generic ?: GenericApiError;

	/**
	 * The 'fields' object is a mapping of field names to their respective validation errors. Each field can have
	 * multiple validation errors, which are represented as an array of error objects. This allows for detailed
	 * error reporting on a per-field basis, making it easier for users to understand and correct their input.
	 *
	 * Field names support dot notation for nested fields (e.g., 'address.street', 'contacts.0.email').
	 *
	 * @example
	 * ```typescript
	 * // Access a specific field's error
	 * const nameError = errorBag.fields['name'];
	 * ```
	 */
	public fields: { [fieldName: string]: FieldValidationError } = {};

	/**
	 * The 'response' property holds the original Axios response object that triggered the error. This can be useful for
	 * debugging purposes, as it contains information about the HTTP status code, headers, and the response data
	 * from the server.
	 *
	 * This property is `null` for network errors where no response was received from the server.
	 */
	public response: AxiosResponse | null = null;

	/**
	 * Creates an ApiErrorMessageBag from an Axios exception.
	 *
	 * This factory method parses the Axios error response and categorizes errors into:
	 * - Generic errors (non-field-specific, like authentication or server errors)
	 * - Field validation errors (specific to form fields)
	 *
	 * Supported HTTP status codes:
	 * - Network errors (ERR_NETWORK): Creates a generic network error message
	 * - 400 Bad Request: Parses validation errors from response body
	 * - 403 Forbidden: Creates an unauthorized action error
	 * - 409 Conflict: Parses conflict errors (same as 400)
	 * - 500 Internal Server Error: Creates a server error message
	 *
	 * For any other status codes, the original exception is re-thrown.
	 *
	 * @param exception - The Axios error object or an existing ApiErrorMessageBag
	 * @returns A new ApiErrorMessageBag instance with parsed errors
	 * @throws The original exception if the HTTP status code is not handled
	 *
	 * @example
	 * ```typescript
	 * try {
	 *   await axios.post('/api/resource', data);
	 * } catch (e) {
	 *   const errorBag = ApiErrorMessageBag.fromAxiosException(e);
	 *   // Handle errors...
	 * }
	 * ```
	 */
	static fromAxiosException(exception: any) {

		if (exception instanceof ApiErrorMessageBag) {
			return exception;
		}

		const error = new ApiErrorMessageBag();
		const response: AxiosResponse = exception.response;
		error.response = response ?? null;

		if (exception.code === 'ERR_NETWORK') {
			error.generic = new GenericApiError([{
				key: 'NETWORK_ERROR',
				parameters: {},
				message: 'A network error occurred. Please check your internet connection and try again.'
			}]);
			return error;
		}

		switch (response?.status) {
			case 400:
			case 409:

				const validationErrors = response.data.errors;

				for (let fieldName in validationErrors) {
					if (!validationErrors.hasOwnProperty(fieldName)) {
						continue;
					}

					if (fieldName === '') {
						if (!Array.isArray(validationErrors[''])) {
							error.generic = new GenericApiError([ validationErrors[''] ]);
						} else {
							error.generic = new GenericApiError(validationErrors['']);
						}
						continue;
					}

					if (!Array.isArray(validationErrors[fieldName])) {
						error.fields[fieldName] = new FieldValidationError(fieldName, [ validationErrors[fieldName] ]);
					} else {
						error.fields[fieldName] = new FieldValidationError(fieldName, validationErrors[fieldName]);
					}

					// -- working around some backend legacy stuff
					// if there is a 'symbol' error, it should be displayed as an icon error
					// (theoretically overwriting the icon error, but normally you can't have both anyway)
					if(error.fields.symbol) {
						error.fields['icon'] = error.fields.symbol;
						delete error.fields.symbol;
					}
					// --
				}
				break;

			case 403:
				error.generic = new GenericApiError([{
					key: 'UNAUTHORIZED_ACTION',
					parameters: {},
					message: 'This action is unauthorized.'
				}])
				break;

			case 500:
				if (typeof (response.data.errors) !== 'undefined') {
					error.generic = new GenericApiError(response?.data.errors['']);
				} else if (response?.data.message) {
					error.generic = new GenericApiError([{
						key: 'INTERNAL_SERVER_ERROR',
						parameters: {},
						message: response?.data.message
					}])
				} else {
					error.generic = new GenericApiError([{
						key: 'INTERNAL_SERVER_ERROR',
						parameters: {},
						message: 'An internal server error occurred.'
					}])
				}
				break;

			// don't process anything that is not in this list.
			default:
				throw exception;
		}

		return error;
	}

	/**
	 * Extracts error message strings from various error formats.
	 *
	 * This utility method normalizes different error formats into a consistent array of strings.
	 * It handles:
	 * - Plain string errors
	 * - Arrays of strings
	 * - Arrays of error objects with a 'message' property
	 * - Single error objects with a 'message' property
	 *
	 * @param error - The error value in any supported format
	 * @returns An array of error message strings
	 *
	 * @example
	 * ```typescript
	 * // String input
	 * getMessageFromObject('Invalid email') // => ['Invalid email']
	 *
	 * // Array of strings
	 * getMessageFromObject(['Error 1', 'Error 2']) // => ['Error 1', 'Error 2']
	 *
	 * // Object with message
	 * getMessageFromObject({ message: 'Not found' }) // => ['Not found']
	 *
	 * // Array of error objects
	 * getMessageFromObject([{ message: 'Error 1' }, { message: 'Error 2' }])
	 * // => ['Error 1', 'Error 2']
	 * ```
	 */
	static getMessageFromObject(error: string | object | any): string[] {
		if (typeof(error) === 'string') {
			return [ error ];
		}

		if (Array.isArray(error)) {
			return error.map((e: any) => {
				if (typeof(e) === 'string') {
					return e;
				} else {
					return e['message'];
				}
			});
		}

		if (typeof(error) === 'object' && typeof(error['message']) !== 'undefined') {
			return [ error['message'] ];
		}

		return [ error ];
	}

	/**
	 * Retrieves the validation error for a specific field using dot notation path.
	 *
	 * This method allows accessing nested field errors by providing path segments
	 * as separate arguments, which are then joined with dots.
	 *
	 * @param path - One or more path segments that form the field name
	 * @returns The FieldValidationError for the specified field, or null if not found
	 *
	 * @example
	 * ```typescript
	 * // Get error for 'email' field
	 * const emailError = errorBag.getForField('email');
	 *
	 * // Get error for nested field 'address.street'
	 * const streetError = errorBag.getForField('address', 'street');
	 *
	 * // Get error for array field 'contacts.0.phone'
	 * const phoneError = errorBag.getForField('contacts', '0', 'phone');
	 * ```
	 */
	getForField(... path: string[]) {
		return this.fields[path.join('.')] || null;
	}

	/**
	 * Converts all errors (generic and field-specific) to a localized string representation.
	 *
	 * This method iterates through all errors in the bag and converts them to their
	 * localized string representation using the provided i18n instance. Each error
	 * is separated by a newline character.
	 *
	 * @param i18n - Optional Vue I18n instance for translation. If not provided, falls back to toString().
	 * @returns A newline-separated string of all localized error messages
	 *
	 * @example
	 * ```typescript
	 * import { useI18n } from 'vue-i18n';
	 *
	 * const { t } = useI18n();
	 * const localizedErrors = errorBag.toLocaleString(i18n);
	 * // Output: "Email is required\nPassword must be at least 8 characters"
	 * ```
	 */
	toLocaleString(i18n?: VueI18n) {
		let errors = [];
		if (this.generic) {
			errors.push(this.generic.toLocaleString(i18n));
		}

		for (let fieldName in this.fields) {
			if(!i18n){
				errors.push(this.fields[fieldName].toString());
				continue;
			}

			if (!this.fields.hasOwnProperty(fieldName)) {
				continue;
			}

			errors.push(this.fields[fieldName].toLocaleString(i18n));
		}
		return errors.join('\n');
	}

	/**
	 * Converts all errors to a string representation.
	 *
	 * This method first attempts to use the global i18n instance for localization.
	 * If i18n is not available, it falls back to the raw error messages.
	 *
	 * Each error (generic and field-specific) is separated by a newline character.
	 *
	 * @returns A newline-separated string of all error messages
	 *
	 * @example
	 * ```typescript
	 * const errorString = errorBag.toString();
	 * console.log(errorString);
	 * // Output: "Email is required\nPassword must be at least 8 characters"
	 * ```
	 */
	toString() {

		// Attempt to use the localized version if we can.
		if (i18n.global) {
			// @ts-ignore
			return this.toLocaleString(i18n.global);
		}

		let errors = [];
		if (this.generic) {
			errors.push(this.generic.toString());
		}

		for (let fieldName in this.fields) {
			if (!this.fields.hasOwnProperty(fieldName)) {
				continue;
			}

			errors.push(this.fields[fieldName].toString());
		}
		return errors.join('\n');
	}
}
